import BaseController from "./base_controller.js";
import { api, APIError } from "/js/services/api.js";

/**
 * Soumission d'une déclaration TVA (§11 ; EPIC-021 / 21.6).
 *
 * V1 : confirmation in-place (pas de modale lourde — la maquette
 * embarque déjà les deux champs « N° d'accusé DGFiP » et « Date » dans
 * une `<section>` dédiée). Le controller :
 *  - active le bouton uniquement si les deux champs sont remplis,
 *  - ouvre une confirmation native + POST sur `/tva/declarations/{id}/submit`,
 *  - dispatche `tva:declaration-submitted` consommé par
 *    `tva-declaration-form` pour basculer la vue.
 */
export default class TvaSubmitModalController extends BaseController {
  static targets = ["receiptNumber", "receiptDate", "submitButton", "error"];

  connect() {
    this._isSubmitting = false;
    this._refreshButtonState();
  }

  validate() {
    this._hideError();
    this._refreshButtonState();
  }

  async open(event) {
    if (event) event.preventDefault();
    if (this._isSubmitting) return;

    const receiptNumber = this._getValue("receiptNumber");
    const receiptDate = this._getValue("receiptDate");
    if (receiptNumber === "" || receiptDate === "") {
      this._showError(
        "Renseignez le numéro et la date d'accusé avant de soumettre.",
      );
      return;
    }

    if (!window.confirm(
      "Confirmer la soumission de la déclaration TVA ? "
        + "Cette action est définitive — un PDF immuable sera archivé "
        + "dans le coffre-fort.",
    )) {
      return;
    }

    const declarationId = this._resolveDeclarationId();
    if (declarationId === null) {
      this._showError("Aucune déclaration courante trouvée.");
      return;
    }

    this._isSubmitting = true;
    this._setSubmitting(true);

    try {
      const response = await api.post(
        `/tva/declarations/${encodeURIComponent(declarationId)}/submit`,
        {
          receiptNumber,
          receiptDate: new Date(receiptDate).toISOString(),
        },
      );
      const updated = response?.data ?? null;
      this.showSuccess("Déclaration soumise et archivée dans le coffre-fort.");
      this.element.dispatchEvent(new CustomEvent("tva:declaration-submitted", {
        bubbles: true,
        detail: { declaration: updated },
      }));
    } catch (error) {
      this._showError(
        error instanceof APIError
          ? error.message
          : "Échec de la soumission de la déclaration.",
      );
    } finally {
      this._isSubmitting = false;
      this._setSubmitting(false);
    }
  }

  _resolveDeclarationId() {
    const form = this.element.querySelector(
      "[data-controller~='tva-declaration-form']",
    );
    const candidate = form?.dataset?.tvaDeclarationFormDeclarationId
      ?? this.element.dataset.declarationId
      ?? this.element.querySelector("a[data-tva-declaration-form-target='downloadLink']")
        ?.getAttribute("href");
    if (!candidate) return null;
    const match = candidate.match(/declarations\/([a-f0-9-]+)\/pdf/i);
    return match ? match[1] : null;
  }

  _getValue(targetName) {
    const target = `${targetName}Target`;
    if (!(target in this)) return "";
    const element = this[target];
    return typeof element?.value === "string" ? element.value.trim() : "";
  }

  _refreshButtonState() {
    if (!this.hasSubmitButtonTarget) return;
    const ready = this._getValue("receiptNumber") !== ""
      && this._getValue("receiptDate") !== "";
    this.submitButtonTarget.disabled = !ready || this._isSubmitting;
  }

  _setSubmitting(isSubmitting) {
    if (!this.hasSubmitButtonTarget) return;
    this.submitButtonTarget.disabled = isSubmitting;
    this.submitButtonTarget.textContent = isSubmitting
      ? "Soumission en cours…"
      : "Enregistrer l'accusé";
  }

  _showError(message) {
    if (!this.hasErrorTarget) return;
    this.errorTarget.textContent = message;
    this.errorTarget.hidden = false;
  }

  _hideError() {
    if (this.hasErrorTarget) this.errorTarget.hidden = true;
  }
}
